"use client";

import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  TextField,
  Stack,
  IconButton,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import { apiService } from "../../app/api";
import { handleAxiosError } from "../../helper/errorHandler";
import { API_FEATURE_LABELS } from "./featureMap";
import { FEATURE_UI_CONFIG } from "./featureUiConfig";
import { fetchLanguages } from "../../helper/languageUtils";

interface ApiUsageItem {
  total_usage: number;
  unit: string | null;
}

interface FeatureUsageResponse {
  feature: string;
  input_language: string | null;
  output_language: string | null;
  range: {
    start: string;
    end: string;
  };
  usage: ApiUsageItem;
}

interface Props {
  user_id: string;
}

const FeatureWiseUsage = ({ user_id }: Props) => {
  const [data, setData] = useState<FeatureUsageResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [languages, setLanguages] = useState<string[]>([]);
  const [filters, setFilters] = useState({
    feature: "stt",
    input_language: "",
    output_language: "",
    start_date: "",
    end_date: "",
  });

  const featureUi = FEATURE_UI_CONFIG[filters.feature];

  useEffect(() => {
    const loadLanguages = async () => {
      const langs = await fetchLanguages();
      setLanguages(langs);
    };

    loadLanguages();
  }, []);

  const handleChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const fetchUsage = useCallback(async () => {
    if (!user_id || !filters.feature) return;

    try {
      setLoading(true);

      const res = await apiService.get(`/api-usage/${user_id}/feature`, {
        params: {
          feature: filters.feature,
          input_language: filters.input_language || undefined,
          output_language: filters.output_language || undefined,
          start_date: filters.start_date || undefined,
          end_date: filters.end_date || undefined,
        },
      });

      setData(res.data);
    } catch (err) {
      handleAxiosError(err);
    } finally {
      setLoading(false);
    }
  }, [user_id, filters]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  return (
    <Box>
      {/* Header */}
      <Typography variant="h6" mb={3}>
        Feature-wise API Usage
      </Typography>

      {/* Filters */}
      <Stack direction="row" spacing={2} flexWrap="wrap" mb={3}>
        {/* Feature */}
        <TextField
          select
          label="Feature"
          value={filters.feature}
          onChange={(e) => {
            const feature = e.target.value;

            setFilters({
              feature,
              input_language: "",
              output_language: "",
              start_date: filters.start_date,
              end_date: filters.end_date,
            });
          }}
          size="small"
          sx={{ minWidth: 200 }}
        >
          {Object.entries(API_FEATURE_LABELS).map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>

        {/* Input Language */}
        {featureUi?.showInput && (
          <Autocomplete
            options={languages.sort((a, b) => a.localeCompare(b))}
            value={filters.input_language}
            onChange={(e, val) => handleChange("input_language", val || "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label={featureUi.inputLabel}
                size="small"
              />
            )}
            sx={{ minWidth: 180, textTransform: "capitalize" }}
            getOptionLabel={(option) => option}
            isOptionEqualToValue={(option, value) => option === value}
          />
        )}

        {/* Output Language */}
        {featureUi?.showOutput && (
          <Autocomplete
            options={languages.sort((a, b) => a.localeCompare(b))}
            value={filters.output_language}
            onChange={(e, val) => handleChange("output_language", val || "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label={featureUi.outputLabel}
                size="small"
              />
            )}
            sx={{ minWidth: 180, textTransform: "capitalize" }}
            getOptionLabel={(option) => option}
            isOptionEqualToValue={(option, value) => option === value}
          />
        )}

        {/* Start Date */}
        <TextField
          label="Start Date"
          type="date"
          size="small"
          value={filters.start_date}
          onChange={(e) => handleChange("start_date", e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        {/* End Date */}
        <TextField
          label="End Date"
          type="date"
          size="small"
          value={filters.end_date}
          onChange={(e) => handleChange("end_date", e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <IconButton
          color="primary"
          onClick={fetchUsage}
          disabled={loading || !filters.feature}
        >
          <RefreshIcon />
        </IconButton>
      </Stack>

      {/* Content */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : !data ? (
        <Typography color="text.secondary" align="center">
          Select a feature to view usage
        </Typography>
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {API_FEATURE_LABELS[data.feature]}
            </Typography>

            <Typography variant="h4" fontWeight={600}>
              {data.usage.unit === "characters"
                ? data.usage.total_usage ?? 0
                : (data.usage.total_usage ?? 0).toFixed(2)}
            </Typography>

            <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
              {data.usage.unit && <Chip label={data.usage.unit} size="small" />}
              {data.input_language && (
                <Chip label={`Input: ${data.input_language}`} size="small" />
              )}
              {data.output_language && (
                <Chip label={`Output: ${data.output_language}`} size="small" />
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default FeatureWiseUsage;
